import { FC } from 'react';
import { Chip, Grid, Button, Box, Typography, Link } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  PAYOUT_STATUS,
  PAYMENT_METHOD,
  PAY_PERIOD,
} from 'src/apis/staff/staffPayout.interfaces';
import {
  useStaffPayoutState,
  useStaffPayoutActions,
} from '../../contexts/staffPayout.context';
import { isEmpty } from 'lodash-es';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { IStaffPayout } from '../../../../../apis/staff/staffPayout.interfaces';
import { format } from 'date-fns';
import { EditButton } from 'src/views/components/simple/AllButtons';

type PayoutGridRow = {
  id: string;
  fullName: string;
  date: string;
  amount: number;
  payout: IStaffPayout;
  status: PAYOUT_STATUS;
};

const StaffPayoutGrid: FC = () => {
  const {
    staffPayoutRecords,
    staffPayoutAppState: { selectedPayoutForEdit, isLoading },
  } = useStaffPayoutState();
  const { setSelectedPayoutForEdit, openModal } = useStaffPayoutActions();
  const { translateText } = useTranslation();

  const getPayoutGridRowsToRender = (): PayoutGridRow[] => {
    if (isEmpty(staffPayoutRecords?.data)) return [];

    return staffPayoutRecords.data.map((payout) => {
      const fullName = `${payout.user.firstName}${payout.user.middleName ? ` ${payout.user.middleName}` : ''} ${payout.user.lastName}`;
      const date = `${payout.month} ${payout.year}`;

      return {
        id: payout.payoutId,
        fullName,
        date,
        amount: payout.amount,
        payout,
        status: payout.status,
        paidOn: payout?.paymentDate,
        processedOn: payout?.processedOn,
        approvedOn: payout?.approvedOn,
      };
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: translateText('staff.payouts.columns.staffName'),
      width: 250,
      flex: 1,
    },
    {
      field: 'date',
      headerName: translateText('staff.payouts.columns.date'),
      width: 150,
    },
    {
      field: 'amount',
      headerName: translateText('staff.payouts.columns.amount'),
      width: 150,
      renderCell: (params) => `₹${params.value.toLocaleString()}`,
    },
    {
      field: 'status',
      headerName: translateText('staff.payouts.columns.status'),
      width: 150,
      renderCell: (params) => (
        <Chip
          size="small"
          label={PAYOUT_STATUS[params.value as keyof typeof PAYOUT_STATUS]}
        />
      ),
    },
    {
      field: 'paidOn',
      headerName: translateText('staff.payouts.columns.paidOn'),
      width: 150,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '---',
    },
    {
      field: 'processedOn',
      headerName: translateText('staff.payouts.columns.processedOn'),
      width: 150,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '---',
    },
    {
      field: 'approvedOn',
      headerName: translateText('staff.payouts.columns.approvedOn'),
      width: 150,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'dd/MM/yyyy') : '---',
    },
  ];

  const handleRowClick = (params: GridRowParams<PayoutGridRow>) => {
    if (params.row?.payout) {
      setSelectedPayoutForEdit(params.row.payout);
    }
  };

  const handleBackToPayouts = () => {
    setSelectedPayoutForEdit();
  };

  const onEditPayoutClickHandler = () => {
    openModal(selectedPayoutForEdit || undefined);
  };

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{ height: '100%', p: { xs: 1, sm: 2 } }}
      data-testid="staff-payout-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: selectedPayoutForEdit ? 'space-between' : 'end',
          gap: { xs: 1, sm: 0 },
          mb: { xs: 1, sm: 2 },
        }}
        data-testid="staff-payout-header"
      >
        {selectedPayoutForEdit ? (
          <>
            <Box
              display="flex"
              alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={{ xs: 1, sm: 2 }}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToPayouts}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  width: { xs: '100%', sm: 'auto' },
                  minHeight: { xs: '44px', sm: 'auto' },
                }}
                data-testid="back-to-payouts-button"
              >
                {translateText('staff.payouts.backToPayouts')}
              </Button>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                }}
                data-testid="payout-details-title"
              >
                {translateText('staff.payouts.payoutDetails')}
              </Typography>
            </Box>
            <Box width={{ xs: '100%', sm: 'auto' }}>
              <EditButton
                label={translateText('staff.payouts.editPayout')}
                onClickHandler={onEditPayoutClickHandler}
                data-testid="edit-payout-button"
              />
            </Box>
          </>
        ) : null}
      </Grid>

      {selectedPayoutForEdit && (
        <Grid item xs={12} data-testid="payout-summary-section">
          <Box
            sx={{
              mb: { xs: 1, sm: 2 },
              p: { xs: 1, sm: 2 },
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('staff.payouts.summary')}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('staff.payouts.columns.staffName')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {`${selectedPayoutForEdit.user.firstName}${selectedPayoutForEdit.user.middleName ? ` ${selectedPayoutForEdit.user.middleName}` : ''} ${selectedPayoutForEdit.user.lastName}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('staff.payouts.columns.date')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {`${selectedPayoutForEdit.month} ${selectedPayoutForEdit.year}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('staff.payouts.columns.amount')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  ₹{selectedPayoutForEdit.amount.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('staff.payouts.columns.status')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  <Chip
                    size="small"
                    label={
                      PAYOUT_STATUS[
                      selectedPayoutForEdit.status as keyof typeof PAYOUT_STATUS
                      ]
                    }
                  />
                </Typography>
              </Grid>
              {selectedPayoutForEdit.paymentDate && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.columns.paidOn')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {format(
                      new Date(selectedPayoutForEdit.paymentDate),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.processedOn && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.columns.processedOn')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {format(
                      new Date(selectedPayoutForEdit.processedOn),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.approvedOn && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.columns.approvedOn')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {format(
                      new Date(selectedPayoutForEdit.approvedOn),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  {translateText('staff.payouts.financialYear')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedPayoutForEdit.financialYear || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  {translateText('staff.payouts.payPeriod')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {PAY_PERIOD[
                    selectedPayoutForEdit.payPeriod as keyof typeof PAY_PERIOD
                  ] || '-'}
                </Typography>
              </Grid>
              {selectedPayoutForEdit.workingDays !== undefined && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.workingDays')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayoutForEdit.workingDays}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.paidDays !== undefined && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.paidDays')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayoutForEdit.paidDays}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.paymentMethod && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.paymentMethod')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {PAYMENT_METHOD[
                      selectedPayoutForEdit.paymentMethod as keyof typeof PAYMENT_METHOD
                    ] || '-'}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.transactionReference && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.transactionReference')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayoutForEdit.transactionReference}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.processedBy && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.processedBy')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayoutForEdit.processedBy}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.approvedBy && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('staff.payouts.approvedBy')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedPayoutForEdit.approvedBy}
                  </Typography>
                </Grid>
              )}
              {selectedPayoutForEdit.salaryBreakdown && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mt: 2 }}
                    >
                      {translateText('staff.payouts.salaryBreakdown')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.basic')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₹
                      {selectedPayoutForEdit.salaryBreakdown.basic.toLocaleString()}
                    </Typography>
                  </Grid>
                  {selectedPayoutForEdit.salaryBreakdown.hra !== undefined && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.hra')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ₹
                        {selectedPayoutForEdit.salaryBreakdown.hra.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayoutForEdit.salaryBreakdown.da !== undefined && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.da')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ₹
                        {selectedPayoutForEdit.salaryBreakdown.da.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayoutForEdit.salaryBreakdown.transportAllowance !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.transportAllowance')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.transportAllowance.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.medicalAllowance !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.medicalAllowance')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.medicalAllowance.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.specialAllowance !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.specialAllowance')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.specialAllowance.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.otherAllowances !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.otherAllowances')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.otherAllowances.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.grossSalary')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₹
                      {selectedPayoutForEdit.salaryBreakdown.grossSalary.toLocaleString()}
                    </Typography>
                  </Grid>
                  {selectedPayoutForEdit.salaryBreakdown.providentFund !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.providentFund')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.providentFund.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown
                    .employeeStateInsurance !== undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.employeeStateInsurance')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.employeeStateInsurance.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.professionalTax !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.professionalTax')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.professionalTax.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.incomeTax !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.incomeTax')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.incomeTax.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  {selectedPayoutForEdit.salaryBreakdown.otherDeductions !==
                    undefined && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('staff.payouts.otherDeductions')}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          ₹
                          {selectedPayoutForEdit.salaryBreakdown.otherDeductions.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.totalDeductions')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₹
                      {selectedPayoutForEdit.salaryBreakdown.totalDeductions.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.netSalary')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="success.main"
                    >
                      ₹
                      {selectedPayoutForEdit.salaryBreakdown.netSalary.toLocaleString()}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedPayoutForEdit.bankDetails && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mt: 2 }}
                    >
                      {translateText('staff.payouts.bankDetails')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.bankName')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPayoutForEdit.bankDetails.bankName}
                    </Typography>
                  </Grid>
                  {selectedPayoutForEdit.bankDetails.branchName && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.branchName')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedPayoutForEdit.bankDetails.branchName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.accountNumber')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPayoutForEdit.bankDetails.accountNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.ifscCode')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPayoutForEdit.bankDetails.ifscCode}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedPayoutForEdit.taxDetails && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mt: 2 }}
                    >
                      {translateText('staff.payouts.taxDetails')}
                    </Typography>
                  </Grid>
                  {selectedPayoutForEdit.taxDetails.pan && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.pan')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedPayoutForEdit.taxDetails.pan}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayoutForEdit.taxDetails.uan && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.uan')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedPayoutForEdit.taxDetails.uan}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayoutForEdit.taxDetails.tdsAmount !== undefined && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.tdsAmount')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ₹
                        {selectedPayoutForEdit.taxDetails.tdsAmount.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayoutForEdit.taxDetails.taxSlab && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="textSecondary">
                        {translateText('staff.payouts.taxSlab')}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {selectedPayoutForEdit.taxDetails.taxSlab}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              {(() => {
                const payslipDoc =
                  selectedPayoutForEdit.document ||
                  selectedPayoutForEdit.payslip;
                if (!payslipDoc) return null;
                const fileUrl = payslipDoc.fileUrl;
                return (
                  <Grid item xs={12} data-testid="payout-payslip-section">
                    <Typography variant="body2" color="textSecondary">
                      {translateText('staff.payouts.payslip')}
                    </Typography>
                    {fileUrl ? (
                      <Box sx={{ mt: 1 }}>
                        <Link
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'inline-block', mb: 1 }}
                          data-testid="payout-payslip-download-link"
                        >
                          {payslipDoc.documentName ||
                            translateText('staff.payouts.viewPayslip')}
                        </Link>
                        <Box
                          component="iframe"
                          src={fileUrl}
                          title={payslipDoc.documentName ||
                            translateText('staff.payouts.viewPayslip')}
                          sx={{
                            width: '100%',
                            minHeight: 400,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                          data-testid="payout-payslip-pdf-viewer"
                        />
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {payslipDoc.documentName || '-'}
                      </Typography>
                    )}
                  </Grid>
                );
              })()}
              {selectedPayoutForEdit.remarks && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {translateText('common.remarks')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayoutForEdit.remarks}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {!selectedPayoutForEdit ? (
        <Grid
          item
          xs={12}
          data-testid="staff-payout-grid-container"
          sx={{
            height: 'calc(100vh - 250px)',
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            rows={getPayoutGridRowsToRender()}
            columns={columns}
            dataTestId="staff-payout-grid"
            onRowClick={selectedPayoutForEdit ? undefined : handleRowClick}
            getRowClassName={() => 'cursor-pointer'}
            loading={isLoading}
            hideColumnsOnMobile={['processedOn', 'approvedOn']}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default StaffPayoutGrid;
